import { supabase } from '@/lib/supabase';

export interface VitalsData {
  bpSystolic: string;
  bpDiastolic: string;
  temperature: string;
  pulse: string;
  respiratoryRate: string;
  oxygenSaturation: string;
  weight: string;
  height: string;
  bmi: string;
  recordedAt: string;
  recordedBy: string;
}

/**
 * Save vitals to patient record (current visit)
 * This persists vitals across page refreshes and navigation
 */
export async function savePatientVitals(patientId: string, vitals: Partial<VitalsData>): Promise<void> {
  try {
    const vitalsData: VitalsData = {
      bpSystolic: vitals.bpSystolic || '',
      bpDiastolic: vitals.bpDiastolic || '',
      temperature: vitals.temperature || '',
      pulse: vitals.pulse || '',
      respiratoryRate: vitals.respiratoryRate || '',
      oxygenSaturation: vitals.oxygenSaturation || '',
      weight: vitals.weight || '',
      height: vitals.height || '',
      bmi: vitals.bmi || '',
      recordedAt: vitals.recordedAt || new Date().toISOString(),
      recordedBy: vitals.recordedBy || 'unknown'
    };

    const { error } = await supabase
      .from('patients')
      .update({ vitals: vitalsData })
      .eq('id', patientId);

    if (error) {
      console.error('Error saving patient vitals:', error);
      throw error;
    }

  } catch (error) {
    console.error('Failed to save patient vitals:', error);
    throw error;
  }
}

/**
 * Get vitals for a patient (current visit)
 */
export async function getPatientVitals(patientId: string): Promise<VitalsData | null> {
  try {
    const { data, error } = await supabase
      .from('patients')
      .select('vitals')
      .eq('id', patientId)
      .single();

    if (error) {
      console.error('Error fetching patient vitals:', error);
      return null;
    }

    return data?.vitals || null;
  } catch (error) {
    console.error('Failed to fetch patient vitals:', error);
    return null;
  }
}

/**
 * Clear vitals from patient record (when prescription is issued)
 * This marks the visit as completed
 */
export async function clearPatientVitals(patientId: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('patients')
      .update({ vitals: null })
      .eq('id', patientId);

    if (error) {
      console.error('Error clearing patient vitals:', error);
      throw error;
    }

  } catch (error) {
    console.error('Failed to clear patient vitals:', error);
    throw error;
  }
}

/**
 * Update vitals in patient record (for real-time editing)
 */
export async function updatePatientVitals(patientId: string, vitals: Partial<VitalsData>): Promise<void> {
  try {
    // First get current vitals
    const currentVitals = await getPatientVitals(patientId);
    
    // Merge with new vitals
    const updatedVitals: VitalsData = {
      bpSystolic: vitals.bpSystolic ?? currentVitals?.bpSystolic ?? '',
      bpDiastolic: vitals.bpDiastolic ?? currentVitals?.bpDiastolic ?? '',
      temperature: vitals.temperature ?? currentVitals?.temperature ?? '',
      pulse: vitals.pulse ?? currentVitals?.pulse ?? '',
      respiratoryRate: vitals.respiratoryRate ?? currentVitals?.respiratoryRate ?? '',
      oxygenSaturation: vitals.oxygenSaturation ?? currentVitals?.oxygenSaturation ?? '',
      weight: vitals.weight ?? currentVitals?.weight ?? '',
      height: vitals.height ?? currentVitals?.height ?? '',
      bmi: vitals.bmi ?? currentVitals?.bmi ?? '',
      recordedAt: new Date().toISOString(),
      recordedBy: vitals.recordedBy ?? currentVitals?.recordedBy ?? 'unknown'
    };

    await savePatientVitals(patientId, updatedVitals);
  } catch (error) {
    console.error('Failed to update patient vitals:', error);
    throw error;
  }
}
